/**
 * ReplayViewer - リプレイ再生ビューア
 *
 * ターン境界スナップショットを切り替えて表示する読み取り専用ビューア。
 * GameBoard を再利用してマップ状態を表示する。
 */

import { useState, useEffect, useMemo } from 'react';
import type { BarbadosGameState } from '../types';
import { GameBoard } from './GameBoard';
import { createPlayerStatsProps } from '../adapters/gameStateToUIProps';
import { fetchReplay, type ReplayResponse } from '../utils/scoreApi';
import { formatMoveLog } from '../utils/moveLogFormatter';
import { BASE_PATH } from '../config';

interface ReplayViewerProps {
  mapType: string;
  rank: number;
  noUndo?: boolean;
}

export function ReplayViewer({ mapType, rank, noUndo = false }: ReplayViewerProps) {
  const [replay, setReplay] = useState<ReplayResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0); // 0=initial, 1..N=snapshots

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchReplay(mapType, rank, noUndo)
      .then((data) => {
        setReplay(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load replay data');
        setLoading(false);
      });
  }, [mapType, rank]);

  // Format move log entries for the selected turn (must be before early returns to satisfy Rules of Hooks)
  const logEntries = useMemo(() => {
    if (!replay || selectedIndex === 0 || !replay.moveLog || replay.moveLog.length === 0) return [];
    const states = replay.moves;
    if (selectedIndex - 1 >= states.length) return [];
    const currentG = states[selectedIndex - 1].G as any;
    const turnNumber = currentG?.actualTurn || selectedIndex;
    return formatMoveLog(replay.moveLog, turnNumber);
  }, [selectedIndex, replay]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-muted-foreground">Loading replay...</div>
      </div>
    );
  }

  if (error || !replay) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-4">
        <div className="text-destructive">{error || 'Replay not found'}</div>
        <button
          className="btn-secondary px-4 py-2 text-sm"
          onClick={() => { window.location.href = `${BASE_PATH}hall-of-honor?map=${mapType}`; }}
        >
          Back to Hall of Honor
        </button>
      </div>
    );
  }

  // Build the list of viewable states: [initialState, ...turnSnapshots]
  const states: Array<{ label: string; G: unknown }> = [];

  if (replay.initialState) {
    states.push({ label: 'Start', G: replay.initialState });
  }

  for (const snapshot of replay.moves) {
    states.push({ label: `T${snapshot.turn}`, G: snapshot.G });
  }

  if (states.length === 0) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-4">
        <div className="text-muted-foreground">No replay data available</div>
        <button
          className="btn-secondary px-4 py-2 text-sm"
          onClick={() => { window.location.href = `${BASE_PATH}hall-of-honor?map=${mapType}`; }}
        >
          Back to Hall of Honor
        </button>
      </div>
    );
  }

  const currentG = states[selectedIndex].G as BarbadosGameState;

  // Create read-only playerStats props
  const playerStatsProps = createPlayerStatsProps(currentG);

  // Replay controls as phaseContent
  const replayControls = (
    <div className="h-full flex flex-col gap-2">
      {/* Header: Player / Score / Rank in compact layout */}
      <div className="bg-muted/50 rounded p-2 text-xs space-y-0.5 shrink-0">
        <div>
          <span className="text-muted-foreground">Player: </span>
          <span className="font-medium">{replay.playerName}</span>
        </div>
        <div className="flex gap-3">
          <span className="text-muted-foreground">Score: <span className="font-bold text-foreground">{replay.finalScore}</span></span>
          <span className="text-muted-foreground">Rank: <span className="font-medium text-foreground">#{replay.rank}</span></span>
        </div>
      </div>

      {/* Turn selector — scrollable if many turns */}
      <div className="shrink-0 space-y-1">
        <div className="text-xs text-muted-foreground font-medium">Turn</div>
        <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
          {states.map((state, idx) => (
            <button
              key={idx}
              className={`px-2 py-0.5 text-xs rounded font-medium transition-colors ${
                selectedIndex === idx
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              onClick={() => setSelectedIndex(idx)}
            >
              {state.label}
            </button>
          ))}
        </div>
      </div>

      {/* Move Log — fills remaining space */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="text-xs text-muted-foreground font-medium mb-1 shrink-0">Move Log</div>
        <div className="flex-1 overflow-y-auto space-y-0.5">
          {selectedIndex > 0 && logEntries.length > 0 ? (
            logEntries.map((entry, i) => (
              <div key={i} className="text-xs flex gap-1">
                <span>{entry.icon}</span>
                <span>{entry.text}</span>
              </div>
            ))
          ) : (
            <div className="text-xs text-muted-foreground/50">
              {selectedIndex === 0 ? 'Select a turn to view moves' : 'No log data'}
            </div>
          )}
        </div>
      </div>

      {/* Navigation + Back — always pinned at bottom */}
      <div className="shrink-0 space-y-2">
        <div className="flex gap-2">
          <button
            className="btn-secondary flex-1 py-1.5 text-xs font-medium"
            disabled={selectedIndex === 0}
            onClick={() => setSelectedIndex(selectedIndex - 1)}
          >
            Prev
          </button>
          <button
            className="btn-secondary flex-1 py-1.5 text-xs font-medium"
            disabled={selectedIndex === states.length - 1}
            onClick={() => setSelectedIndex(selectedIndex + 1)}
          >
            Next
          </button>
        </div>
        <button
          className="btn-secondary w-full py-1.5 text-xs font-medium"
          onClick={() => { window.location.href = `${BASE_PATH}hall-of-honor?map=${mapType}`; }}
        >
          Back to Hall of Honor
        </button>
      </div>
    </div>
  );

  return (
    <GameBoard
      hexes={currentG.map}
      goodsDisplay={currentG.goodsDisplay}
      phaseContent={replayControls}
      playerStatsProps={playerStatsProps}
      phaseName="Replay"
      currentTurn={currentG.actualTurn || 1}
      mapName={currentG.mapName}
      showGoodsDisplay={false}
      onSetShowGoodsDisplay={() => {}}
      showGoodsDisplayButton={false}
      ferryLinks={currentG.ferryLinks?.map(link => ({
        id: link.id,
        from: link.from,
        to: link.to,
        cost: link.cost,
        ownerColor: link.owner ? currentG.players[link.owner]?.color ?? null : null,
      }))}
      adjacentLinks={currentG.adjacentLinks?.map(link => ({
        id: link.id,
        from: link.from,
        to: link.to,
        cost: link.cost,
        ownerColor: link.owner ? currentG.players[link.owner]?.color ?? null : null,
      }))}
      initialOrientation={currentG.mapConfig?.initialOrientation}
      onResetGame={() => { window.location.href = BASE_PATH; }}
    />
  );
}
